import React from 'react';
import './add-events-to-another-calendar-modal.scss';
import { Button, ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../components/Rodal/Rodal';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/config';
import { faClone } from '@fortawesome/free-solid-svg-icons';
import createNotification from '../../utils/createNotification';
import CRC32 from 'crc-32';

export interface AddEventsToAnotherCalendarModalProps {
    isVisible: boolean;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    calendarId: string;
    header: string;
    link: string;
}

export const AddEventsToAnotherCalendarModal = (
    props: AddEventsToAnotherCalendarModalProps
) => {
    const getCalendarSubscriptionLink = () => {
        const code = (CRC32.str(props.calendarId) >>> 0).toString(16);
        let url = BASE_URL == '/' ? window.location.origin : BASE_URL;
        const link = `${url}/api/${props.link}/${code}`;
        return link;
    };

    const copyUrl = () => {
        navigator.clipboard.writeText(getCalendarSubscriptionLink());
        createNotification('Subscription Link copied successfully', 'success');
    };

    return (
        <div className="add-events-to-another-calendar-modal">
            <Rodal
                visible={props.isVisible}
                width={600}
                onClose={() => {
                    props.setIsVisible(false);
                }}
                animation={'fade'}
                showMask={true}>
                <div>
                    <ModalHeader>{props.header}</ModalHeader>

                    <ModalBody>
                        <div>
                            <div className="subtitle">
                                <div>Calendar Subscription Link:</div>
                                <div className="copy-to-clipboard">
                                    <input
                                        type="text"
                                        readOnly={true}
                                        value={getCalendarSubscriptionLink()}
                                    />
                                    <Button
                                        onClick={copyUrl}
                                        outline
                                        color="success">
                                        <FontAwesomeIcon icon={faClone} />
                                    </Button>
                                </div>
                            </div>
                            <Accordion className="accordion-item">
                                <AccordionSummary
                                    className="accordion-header"
                                    expandIcon={
                                        <FontAwesomeIcon
                                            icon={faPlus}></FontAwesomeIcon>
                                    }
                                    aria-controls="panel1a-content"
                                    id="panel1a-header">
                                    <h6>Outlook</h6>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className="accordion-body">
                                        <h5>Add Internet Calendar</h5>
                                        <ol>
                                            <li>Open your Outlook calendar.</li>
                                            <li>
                                                On the left, select{' '}
                                                <b>Add Calendar</b> {'>'}{' '}
                                                <b>From Internet.</b>
                                            </li>
                                            <li>
                                                Paste subscription link and add
                                                configuration if it's required.
                                            </li>
                                            <li>Confirm calendar import.</li>
                                        </ol>
                                        <div>
                                            Your calendar items appear and will
                                            synchronize. Any calendar can be
                                            turned on/off in the{' '}
                                            <b>Folder Pane</b>. Select the arrow
                                            to overlay the calendar if you want.
                                        </div>
                                        <br></br>
                                        <a href="https://support.microsoft.com/en-us/office/import-calendars-into-outlook-8e8364e1-400e-4c0f-a573-fe76b5a2d379">
                                            Learn more.
                                        </a>
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion className="accordion-item">
                                <AccordionSummary
                                    className="accordion-header"
                                    expandIcon={
                                        <FontAwesomeIcon
                                            icon={faPlus}></FontAwesomeIcon>
                                    }
                                    aria-controls="panel2a-content"
                                    id="panel2a-header">
                                    <h6>Google Calendar (Gmail)</h6>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className="accordion-body">
                                        <h5>
                                            Use a link to add a public calendar
                                        </h5>
                                        <div>
                                            <b>Important:</b> You can only add a
                                            calendar with a link if other
                                            person`s calendar is public.{' '}
                                            <a href="https://support.google.com/calendar/answer/37083?sjid=18001269613485057508-EU">
                                                Learn more about public
                                                calendars
                                            </a>
                                        </div>
                                        <ol>
                                            <li>
                                                On your computer, open{' '}
                                                <a href="https://calendar.google.com/calendar/u/0/r?pli=1">
                                                    Google Calendar
                                                </a>
                                                .
                                            </li>
                                            <li>
                                                On the left, next to "Other
                                                calendars", click Add + {'>'}{' '}
                                                <b>From URL</b>.
                                            </li>
                                            <li>
                                                Enter the{' '}
                                                <a href="https://support.google.com/calendar/answer/37083?sjid=18001269613485057508-EU#link&zippy=%2Cshare-a-link">
                                                    calendar`s address
                                                </a>
                                                .
                                            </li>
                                            <li>
                                                Click <b>Add calendar.</b> The
                                                calendar appears on the left,
                                                under "Other calendars"
                                            </li>
                                        </ol>
                                        <div>
                                            <b>Tip:</b> It might take up to 12
                                            hours for chanes to show in your
                                            Google Calendar.
                                        </div>
                                        <br></br>
                                        <a href="https://support.google.com/calendar/answer/37100?hl=en&co=GENIE.Platform%3DDesktop#:~:text=Use%20an%20email%20address%20to%20subscribe%20to%20a%20calendar&text=On%20your%20computer%2C%20open%20Google%20Calendar.&text=Subscribe%20to%20calendar.,select%20one%20from%20the%20list.">
                                            Learn more.
                                        </a>
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion className="accordion-item">
                                <AccordionSummary
                                    className="accordion-header"
                                    expandIcon={
                                        <FontAwesomeIcon
                                            icon={faPlus}></FontAwesomeIcon>
                                    }
                                    aria-controls="panel3a-content"
                                    id="panel3a-header">
                                    <h6>Apple Calendar</h6>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div className="accordion-body">
                                        <h5>Subscribe to a calendar</h5>
                                        <div>
                                            To subscribe to a calendar from a
                                            link on the internet or an email you
                                            received, click the link. If you do
                                            this, you can skip step 1, and the
                                            calendar`s web address is filled out
                                            for you in step 2.
                                        </div>
                                        <ol>
                                            <li>
                                                In the Calendar app on your Mac,
                                                choose File {'>'} New Calendar
                                                Subscription.
                                            </li>
                                            <li>
                                                Enter the calendar`s web
                                                address, then click Subscribe.
                                            </li>
                                            <li>
                                                Enter a name for the calendar in
                                                the Name field, then click the
                                                adjacent pop-up menu and choose
                                                a color.
                                            </li>
                                            <li>
                                                Click the Location pop-up menu,
                                                then choose an account for the
                                                subscription.
                                                <ul>
                                                    <li>
                                                        If you choose your
                                                        iCloud account, the
                                                        calendar is available on
                                                        all your computers and
                                                        devices that are set up
                                                        with iCloud.
                                                    </li>
                                                    <li>
                                                        If you choose On My Mac,
                                                        the calendar is saved on
                                                        your computer.{' '}
                                                    </li>
                                                </ul>
                                            </li>
                                            <li>
                                                To get the calendar`s event
                                                attachments or alerts, deselect
                                                the appropriate Remove
                                                checkboxes.
                                            </li>
                                            <li>
                                                Click the Auto-refresh pop-up
                                                menu, then choose how often to
                                                update the calendar.
                                            </li>
                                            <li>
                                                To prevent alerts from appearing
                                                for this calendar, select
                                                “Ignore alerts.”
                                            </li>
                                            <li>Click OK.</li>
                                        </ol>
                                        <div>
                                            To make changes later, click the
                                            name of the calendar, then choose
                                            Edit {'>'} Get Info.
                                        </div>
                                        <br></br>
                                        <a href="https://support.apple.com/guide/calendar/subscribe-to-calendars-icl1022/mac">
                                            Learn more.
                                        </a>
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        </div>
                    </ModalBody>
                </div>
            </Rodal>
        </div>
    );
};
